import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, TrendingUp, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";

const money = (n) => (n === null || n === undefined || n === "" ? "—" : "$" + Number(n).toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 2 }));
const blank = { product: "", sku: "", old_price: "", new_price: "", effective_on: "", note: "" };

export default function PriceHistory() {
  const [changes, setChanges] = useState(null);
  const [form, setForm] = useState(blank);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/staff/price-history")
    .then(({ data }) => setChanges(data.changes))
    .catch((e) => toast.error(formatApiError(e, "Could not load the price history")));

  useEffect(() => { load(); }, []);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        product: form.product,
        sku: form.sku || null,
        old_price: form.old_price === "" ? null : Number(form.old_price),
        new_price: form.new_price === "" ? null : Number(form.new_price),
        effective_on: form.effective_on || null,
        note: form.note || null,
      };
      const { data } = await api.post("/staff/price-history", body);
      setChanges((prev) => [data, ...(prev || [])]);
      setForm(blank);
      setOpen(false);
      toast.success("Price change recorded");
    } catch (err) { toast.error(formatApiError(err, "Could not record this change")); }
    finally { setSaving(false); }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Remove the ${entry.product} entry from the price history?`)) return;
    try {
      await api.delete(`/staff/price-history/${entry.id}`);
      setChanges((prev) => prev.filter((c) => c.id !== entry.id));
      toast.success("Entry removed");
    } catch (e) { toast.error(formatApiError(e, "Could not remove this entry")); }
  };

  return (
    <div>
      <Seo title="Price history — EI Battery Workspace" />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <span className="ei-eyebrow">Pricing</span>
          <h1 className="mt-1 text-2xl sm:text-3xl font-bold text-ei-navy">Price history</h1>
          <p className="mt-2 text-sm text-ei-graphite/70 max-w-2xl">A dated record of every catalogue price change — what it was, what it became and who asked for it. All figures are ex GST.</p>
        </div>
        <button onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-2 font-display text-sm font-bold bg-ei-navy text-ei-white px-4 py-2.5 rounded-sm hover:bg-ei-ink transition-colors min-h-[44px]" data-testid="price-history-add-toggle">
          <Plus className="w-4 h-4" /> Record a change
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="mt-5 bg-white border border-ei-mist rounded-md p-5 grid gap-3 sm:grid-cols-2" data-testid="price-history-form">
          <input required value={form.product} onChange={set("product")} placeholder="Product name" className="rounded-sm border border-ei-mist px-3 py-2 text-sm sm:col-span-2" data-testid="price-history-product" />
          <input value={form.sku} onChange={set("sku")} placeholder="Model / SKU (optional)" className="rounded-sm border border-ei-mist px-3 py-2 text-sm" data-testid="price-history-sku" />
          <input type="date" value={form.effective_on} onChange={set("effective_on")} className="rounded-sm border border-ei-mist px-3 py-2 text-sm" data-testid="price-history-date" />
          <input type="number" step="0.01" min="0" value={form.old_price} onChange={set("old_price")} placeholder="Old price ex GST" className="rounded-sm border border-ei-mist px-3 py-2 text-sm tnum" data-testid="price-history-old" />
          <input type="number" step="0.01" min="0" value={form.new_price} onChange={set("new_price")} placeholder="New price ex GST" className="rounded-sm border border-ei-mist px-3 py-2 text-sm tnum" data-testid="price-history-new" />
          <textarea value={form.note} onChange={set("note")} rows={2} placeholder="Reason / who asked for it" className="rounded-sm border border-ei-mist px-3 py-2 text-sm sm:col-span-2" data-testid="price-history-note" />
          <div className="sm:col-span-2 flex gap-2">
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 font-display text-sm font-bold bg-ei-navy text-ei-white px-4 py-2.5 rounded-sm hover:bg-ei-ink min-h-[44px] disabled:opacity-60" data-testid="price-history-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save entry
            </button>
            <button type="button" onClick={() => { setOpen(false); setForm(blank); }} className="font-display text-sm font-bold text-ei-navy border border-ei-navy/25 px-4 py-2.5 rounded-sm hover:border-ei-navy min-h-[44px]" data-testid="price-history-cancel">Cancel</button>
          </div>
        </form>
      )}

      <div className="mt-6" data-testid="price-history-list">
        {changes === null ? (
          <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-ei-navy" /></div>
        ) : changes.length === 0 ? (
          <div className="bg-white border border-ei-mist rounded-md p-16 text-center text-ei-graphite/60" data-testid="price-history-empty">
            <TrendingUp className="w-12 h-12 mx-auto text-ei-mist" />
            <p className="mt-4 font-semibold text-ei-navy">No price changes recorded yet</p>
          </div>
        ) : (
          <div className="bg-white border border-ei-mist rounded-md overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ei-graphite/60 border-b border-ei-mist">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3 text-right">Was</th>
                  <th className="px-4 py-3 text-right">Now</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {changes.map((c) => {
                  const up = c.old_price != null && c.new_price != null && c.new_price > c.old_price;
                  const down = c.old_price != null && c.new_price != null && c.new_price < c.old_price;
                  return (
                    <tr key={c.id} className="border-b border-ei-mist/60 last:border-0 align-top" data-testid={`price-history-row-${c.id}`}>
                      <td className="px-4 py-3 whitespace-nowrap tnum text-ei-graphite/70">{c.effective_on}</td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-ei-navy">{c.product}</span>
                        {c.sku && <span className="block text-xs text-ei-graphite/60">{c.sku}</span>}
                      </td>
                      <td className="px-4 py-3 text-right tnum text-ei-graphite/60 line-through">{money(c.old_price)}</td>
                      <td className={`px-4 py-3 text-right tnum font-semibold ${up ? "text-red-700" : down ? "text-emerald-700" : "text-ei-navy"}`} data-testid={`price-history-new-${c.id}`}>{money(c.new_price)}</td>
                      <td className="px-4 py-3 text-xs text-ei-graphite/70">{c.note}{c.recorded_by && <span className="block text-ei-graphite/50">Recorded by {c.recorded_by}</span>}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => remove(c)} className="text-red-700 hover:bg-red-50 p-2 rounded-sm" aria-label={`Remove ${c.product} entry`} data-testid={`price-history-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
